import * as React from 'react';
import Styled, { css, ThemeContext } from 'styled-components';

import { media, VALID_BUTTON_STYLE } from '../../lib/StyledComponents';
import { Spinner } from '../Spinner';
import { LoadingSpinner } from '../_ThemeTwo_/Spinner/LoadingSpinner';
import { normalizeColor } from '../_ThemeTwo_/Utils/Styles/normalizeColor';

import Button from './Button';
import { IButtonProps } from './common';
import { BaseButtonStyled } from './BaseButton';

const ButtonSpinnerWrap = Styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${media.small`
    flex-direction: row;
  `}

  ${BaseButtonStyled}:disabled {
    cursor: not-allowed;
  }
`;

ButtonSpinnerWrap.displayName = 'ButtonSpinnerWrap';

interface IButtonSpinnerLoading {
  hasLoadingIconInside?: boolean;
  buttonStyle: VALID_BUTTON_STYLE;
}

const ButtonSpinnerLoading = Styled.div<IButtonSpinnerLoading>`
  margin-top: 1em;
  line-height: 0;

  ${media.small`
    margin-top: 0;
    margin-left: 1em;
  `}

  ${({ hasLoadingIconInside, buttonStyle, theme }) => {
    if (hasLoadingIconInside) {
      const variant = theme.button[buttonStyle];
      const colorNormalize = normalizeColor(theme.themeTwo);

      return css`
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0;

        & > ${LoadingSpinner} {
          #loading-circle-outline {
            stroke: ${variant.spinner && colorNormalize(variant.spinner.color.circleOutline)};
          }

          ${theme.themeTwo.spinner.withOutline ? css`
            #animation-outline {
              stroke: ${variant.spinner && colorNormalize(variant.spinner.color.animatedOutline)};
            }
          ` : css`
              stroke: transparent;
          `}
        }

        ${media.small`
          margin: 0;
        `}
      `;
    }
  }}
`;

ButtonSpinnerLoading.displayName = 'ButtonSpinnerLoading';

export interface IButtonSpinnerProps extends IButtonProps {
  loading: boolean;
}

export const ButtonWithSpinner: React.FunctionComponent<React.HTMLAttributes<HTMLButtonElement> & IButtonSpinnerProps> =
  ({ loading, children, ...props }) => {
    const { button } = React.useContext(ThemeContext);
    const buttonStyle = props.buttonStyle ? props.buttonStyle : 'primary';

    return (
      <ButtonSpinnerWrap>
        <Button
          inline={true}
          aria-live={loading ? 'polite' : undefined}
          aria-label={loading ? 'Loading do not refresh the page' : undefined}
          disabled={loading ? true : props.disabled}
          isLoading={loading ? true : false}
          {...props}
        >

          {loading && button[buttonStyle].loadingIconInsideButton ?
            <ButtonSpinnerLoading hasLoadingIconInside={true} buttonStyle={buttonStyle} >
              {/* tslint:disable-next-line: no-non-null-assertion */}
              <LoadingSpinner size={button[buttonStyle].spinner && button[buttonStyle].spinner!.size} />
            </ButtonSpinnerLoading>
            :
            <>
              {children}
            </>
          }
        </Button>

        {loading && !button[buttonStyle].loadingIconInsideButton && (
          <ButtonSpinnerLoading buttonStyle={buttonStyle}>
            <Spinner small={true} />
          </ButtonSpinnerLoading>
        )}
      </ButtonSpinnerWrap>
    );
};

export default ButtonWithSpinner;
